import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { BillingApiController } from '../../../api-controllers/billing.api-controller';
import { TranslateService } from '@ngx-translate/core';
import { OrganizationManager } from '../../../managers/organization.manager';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { LoggingService } from '../../../../../../modules/src/lib/core/services/logging.service';
import { v4 as uuid } from 'uuid';
import { StripeManager } from '../../../managers/stripe.manager';
import { AuthService } from '@auth0/auth0-angular';
import { take } from 'rxjs/operators';
import { differenceInSeconds } from 'date-fns';
import {
  MagienoObservabilityManager,
} from '../../../../../../modules/src/lib/core/managers/magieno-observability.manager';

declare global {
  interface Window {
    Stripe: any;
  }
}

@Component({
  selector: 'app-subscription-created',
  templateUrl: './subscription-created.component.html',
  styleUrls: ['./subscription-created.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class SubscriptionCreatedComponent implements OnInit, OnDestroy {

  interval: any;

  stripe: any;

  timeWhenWeLandedOnThisPage?: Date;

  user;

  constructor(private readonly organizationManager: OrganizationManager,
              private readonly router: Router,
              private readonly route: ActivatedRoute,
              public readonly translateService: TranslateService,
              public readonly billingApiController: BillingApiController,
              private readonly loggingService: LoggingService,
              private readonly stripeManager: StripeManager,
              private readonly observabilityManager: MagienoObservabilityManager,
              public auth: AuthService,
  ) {
  }

  async ngOnInit() {
    this.auth.user$.pipe(take(1)).subscribe(user => {
      this.stripe = window.Stripe(this.stripeManager.getPublishableKey(user.email), { locale: this.translateService.currentLang });
      this.user = user;

      // Retrieve the parameters from the query param and retrieve
      this.route.queryParams
        .subscribe(async params => {
            await this.finalizeSubscription(params);
          },
        );
    });
  }

  async finalizeSubscription(params: Params) {
    try {
      const licenseType = params.licenseType;
      const setupFreeTrial = params.setupFreeTrial === 'true';
      const setupIntent = params.setup_intent;
      const setupIntentClientSecret = params.setup_intent_client_secret;
      const redirectStatus = params.redirect_status;

      if (redirectStatus === 'error') {
        await this.observabilityManager.sendAlert('CRITICAL', 'subscription-created - Error with Stripe on redirect', {
          user: this.user,
          params,
        });

        this.loggingService.error('Error with Stripe', { params });
      }

      let paymentMethodId;

      if (setupIntentClientSecret) {
        if (this.stripe === undefined) {
          this.loggingService.error('Stripe is not defined');
          await this.observabilityManager.sendAlert('CRITICAL', 'subscription-created - Error stripe is not defined', {
            user: this.user,
            params,
          });
        }
        try {
          const setupIntentResult = await this.stripe.retrieveSetupIntent(setupIntentClientSecret);
          paymentMethodId = setupIntentResult.setupIntent.payment_method;
        } catch (error) {
          this.loggingService.error('Error retrieving stripe setup intent', { error });
          await this.observabilityManager.sendAlert('CRITICAL', 'subscription-created - Error retrieving stripe setup intent', {
            user: this.user,
            params,
            error,
          });
          return;
        }
      }

      const idempotencyKey = uuid();

      await this.billingApiController.finalizeSubscription({
        licenseType,
        paymentMethodId,
        setupFreeTrial: false,
      }, idempotencyKey);

      this.interval = setInterval(async () => {
        await this.refresh();
      }, 1000);
      this.timeWhenWeLandedOnThisPage = new Date();
    } catch (error) {
      this.loggingService.error('Error when trying to finalize the subscription', { error });
      await this.observabilityManager.sendAlert('CRITICAL', 'subscription-created - Error when trying to finalize the subscription', {
        user: this.user,
        params,
        error,
      });
    }
  }

  async ngOnDestroy() {
    clearInterval(this.interval);
  }

  async refresh() {
    if (this.timeWhenWeLandedOnThisPage !== undefined && differenceInSeconds(new Date(), this.timeWhenWeLandedOnThisPage) > 10) {
      await this.observabilityManager.sendAlert('CRITICAL', 'subscription-created - Waiting for activation for more than 10 seconds.', {
        user: this.user,
      });

      // Stop notifying.
      this.timeWhenWeLandedOnThisPage = undefined;
    } else if (this.timeWhenWeLandedOnThisPage !== undefined && differenceInSeconds(new Date(), this.timeWhenWeLandedOnThisPage) > 5) {
      await this.observabilityManager.sendAlert('CRITICAL', 'subscription-created - Waiting for activation for more than 5 seconds.', {
        user: this.user,
      });
    }

    const organization = await this.organizationManager.refresh();

    if (organization.activeLicense !== undefined) {
      clearInterval(this.interval);
      await this.router.navigateByUrl('/');
    }
  }
}
